import { useCallback } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { axiosClient } from "$/http"

import Urls from "@/api/urls"
import { CoachingGoalStatus } from "@/constants"

import { QueryKey } from "../consts"
import { BehavioralPractice, BehavioralPracticeApi } from "../model/behavioral-goal"

const getBehavioralPractice = async ({ queryKey }): Promise<BehavioralPracticeApi[]> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_key, { behavioralChangeId, participantId, query }] = queryKey
    const { data } = await axiosClient.get(Urls.behavioralPractice(participantId, behavioralChangeId, query))
    return data
}

export function useBehavioralPracticeData(
    participantId: number,
    behavioralChangeId: number,
    query: CoachingGoalStatus
) {
    return useQuery<BehavioralPracticeApi[], AxiosError, BehavioralPractice[]>({
        queryKey: [QueryKey.BehavioralPractice, { participantId, behavioralChangeId, query }],
        queryFn: getBehavioralPractice,
        select: useCallback(data => data.map(item => new BehavioralPractice(item)), [])
    })
}

export function useArchivedBehavioralPracticeData(participantId: number, behavioralChangeId: number) {
    return useQuery<BehavioralPracticeApi[], AxiosError, number[]>({
        queryKey: [QueryKey.BehavioralPractice, { participantId, behavioralChangeId, query: "archived" }],
        queryFn: getBehavioralPractice,
        select: useCallback(data => data.map(item => item.id), [])
    })
}

export function useCreateBehavioralPractice(participantId: number) {
    return useMutation({
        mutationFn: (
            behavioralPractice: Pick<BehavioralPractice, "description" | "order" | "status" | "behavioralChangeId">
        ) => {
            const { behavioralChangeId, ...data } = behavioralPractice
            return axiosClient.post<{ id: number }>(
                Urls.createBehavioralPractice(participantId, behavioralChangeId),
                data
            )
        }
    })
}

export function useEditBehavioralPractice(participantId: number) {
    return useMutation({
        mutationFn: (
            behavioralPractice: Pick<
                BehavioralPractice,
                "description" | "order" | "status" | "behavioralChangeId" | "id"
            >
        ) => {
            const { behavioralChangeId, id, ...data } = behavioralPractice
            return axiosClient.put(Urls.editBehavioralPractice(participantId, behavioralChangeId, id), data)
        }
    })
}

export function useArchiveBehavioralPractice(participantId: number) {
    return useMutation({
        mutationFn: ({ behavioralChangeId, practiceId }: { behavioralChangeId: number; practiceId: number }) =>
            axiosClient.post(Urls.archiveBehavioralPractice(participantId, behavioralChangeId, practiceId))
    })
}

export function useUnarchiveBehavioralPractice(participantId: number) {
    return useMutation({
        mutationFn: ({ behavioralChangeId, practiceId }: { behavioralChangeId: number; practiceId: number }) =>
            axiosClient.post(Urls.unarchiveBehavioralPractice(participantId, behavioralChangeId, practiceId))
    })
}

export function useSortBehavioralPractice(participantId: number) {
    return useMutation({
        mutationFn: (data: { ids: number[]; behavioralChangeId: number }) => {
            const { behavioralChangeId, ids } = data
            return axiosClient.post(Urls.sortBehavioralPractice(participantId, behavioralChangeId), { ids })
        }
    })
}
